<script>
  import { onMount, afterUpdate } from 'svelte';
  import { _, locale } from 'svelte-intl';
  import { stores } from '@sapper/app';
  import formatPrice from '../../helpers/formatPrice';
  import { noop } from 'svelte/internal';

  const { session } = stores();

  export let bundle; // Bundle
  export let onPurchaseSuccess; // () => void
  export let onPurchaseError = noop; // (error) => void
  export let onCreateStripeIntent; // (id: string) => void

  const CARD_STYLES = {
    base: {
      color: '#000',
      fontSize: '20px',
      fontFamily: 'LabGrotesque, Helvetica, Arial, sans-serif',
      fontSmoothing: 'antialiased',
      '::placeholder': {
        color: '#BEC3CE'
      }
    },
    invalid: {
      color: '#F94A13',
      iconColor: '#F94A13'
    }
  };

  let stripe;
  let error;
  let stripeMounted = false;
  let loading = false;
  let paymentButtonIsHidden = true;
  let cardIsHidden = true;
  let cardElement;

  $: hasError = !!(error && error.textContent);

  onMount(() => {
    stripe = window.Stripe($session.stripeKey);
  });

  afterUpdate(async () => {
    if (stripeMounted) return;

    const paymentRequest = stripe.paymentRequest({
      country: 'GB',
      currency: bundle.priceCurrency.toLowerCase(),
      total: {
        label: bundle.title,
        amount: bundle.priceAmount
      }
    });

    const canMakePayment = await paymentRequest.canMakePayment();

    if (canMakePayment) {
      paymentButtonIsHidden = false;

      paymentRequest.on('paymentmethod', async (event) => {
        loading = true;
        const { paymentIntent } = await createStripeIntent();
        const { error } = await stripe.confirmPaymentIntent(paymentIntent.clientSecret, {
          payment_method: event.paymentMethod.id
        });

        if (error) {
          loading = false;
          event.complete('fail');
          return;
        }

        event.complete('success');
        handlePayment(paymentIntent);
      });

      const prButton = stripe
        .elements()
        .create('paymentRequestButton', { paymentRequest });

      prButton.mount('#payment-request-button');
    } else {
      cardIsHidden = false;

      cardElement = stripe
        .elements()
        .create('card', { style: CARD_STYLES });

      cardElement.addEventListener('change', (event) => {
        error.textContent = event.error ? event.error.message : '';
      });

      cardElement.mount('#card-element');
    }
    stripeMounted = true;
  });

  function createStripeIntent() {
    return onCreateStripeIntent(bundle.id);
  }

  async function handlePayment(paymentIntent) {
    const { error } = await stripe.handleCardPayment(paymentIntent.clientSecret, cardElement);

    if (error) {
      loading = false;
      onPurchaseError(error);
      return;
    }

    loading = false;
    onPurchaseSuccess(bundle);
  }

  async function submitForm() {
    loading = true;
    const { paymentIntent } = await createStripeIntent();
    handlePayment(paymentIntent);
  }
</script>

<style lang="stylus">
  p
    font-size: 18px
    line-height: 1.5
    color: $warm-grey
    margin: 0 0 40px

  button
    cursor: pointer
    font-size: 20px
    font-family: $font-lab-grotesque
    display: block
    box-sizing: border-box
    background: $violet
    border-radius: 8px
    text-align: center
    border: none
    outline: none
    padding: 16px 0
    color: $bg-color
    width: 100%
    margin: 16px 0 0
    transition: background-color 0.2s ease-in-out

    &[disabled]
      cursor: default
      opacity: 0.5

    @media $bp.desktop
      &:not([disabled]):hover
        background: darken($violet, 10%)

  .card-element
    background: #F0F3F7
    border-radius: 8px
    padding: 16px 20px

  .card-error
    color: $error-color
    margin: 8px 0 0

  .hidden
    display: none

  .powered-by
    display block
    font-size 12px
    color $light-grey
    text-align center
    margin-top 12px
</style>

<p>{$_('fill_bank_details')}</p>
<form class:hidden={cardIsHidden} on:submit|preventDefault={submitForm}>
  <div id="card-element" class="card-element" />
  <div bind:this={error} role="alert" class="card-error" />
  <button disabled={loading || hasError}>
    {$_('pay', { price: formatPrice($locale, bundle.priceCurrency, bundle.priceAmount / 100) })}
  </button>
</form>
<div class:hidden={paymentButtonIsHidden} id="payment-request-button" />
<a href="https://stripe.com" rel="external" target="_blank" class="powered-by">Powered by Stripe</a>
